import { lazy, Suspense, useEffect, useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { clearChunkReloadGuard, withChunkRetry } from "./lib/chunk-recovery";
import { AppShell, PageHeader } from "./components/AppShell";
import { ConfirmProvider } from "./components/ConfirmationModal";
import { SignInPage } from "./pages/SignInPage";
import { SignUpPage } from "./pages/SignUpPage";
import { EmptyState } from "./components/DataTable";
import { isSignedIn, onSessionChange } from "./lib/session";

/**
 * ═══ The sixteen screens are loaded ON DEMAND ═════════════════════════════════
 *
 * These were sixteen static imports, which meant ONE chunk containing all of them: 516 kB of
 * JavaScript that had to be downloaded, parsed and executed before anything appeared. That is the
 * whole of "the Email platform takes too long to load".
 *
 * Two things made it worse than the number suggests:
 *
 *   · The four largest screens — Templates, Campaigns, Domains and Automations, about 4,700 lines
 *     between them — were in that chunk for everybody, including someone who only ever opens the
 *     Overview.
 *   · The SIGN-IN screen was behind it too. An unauthenticated visitor waited for the entire
 *     authenticated console to arrive before a login form could render, which is the slowest possible
 *     first impression and the one every cold visit gets.
 *
 * ─── Why the auth screens stay eager ───
 *
 * SignInPage and SignUpPage are deliberately NOT lazy. They are small (837 lines together) and one of
 * them is the first thing rendered on every cold visit with no session, so splitting them would add a
 * second network round trip to the most common path — trading the bug for a smaller version of itself.
 *
 * Everything behind the session is lazy, because which of those sixteen you need is not known until
 * the route is known, and you never need more than one.
 *
 * ─── Why the mapping is spelled out ───
 *
 * Each page is a NAMED export, and `lazy` wants a module whose `default` is the component — hence the
 * `.then` that renames it. Written out per page rather than hidden in a helper so that a page added
 * later is an obvious one-line addition in the same shape, and cannot accidentally be added as a
 * static import next to them without looking wrong.
 */
const OverviewPage      = lazy(withChunkRetry(() => import("./pages/OverviewPage").then(m => ({ default: m.OverviewPage }))));
const LogsPage          = lazy(withChunkRetry(() => import("./pages/LogsPage").then(m => ({ default: m.LogsPage }))));
const ConfigurationPage = lazy(withChunkRetry(() => import("./pages/ConfigurationPage").then(m => ({ default: m.ConfigurationPage }))));
const DomainsPage       = lazy(withChunkRetry(() => import("./pages/DomainsPage").then(m => ({ default: m.DomainsPage }))));
const SuppressionsPage  = lazy(withChunkRetry(() => import("./pages/SuppressionsPage").then(m => ({ default: m.SuppressionsPage }))));
const TemplatesPage     = lazy(withChunkRetry(() => import("./pages/TemplatesPage").then(m => ({ default: m.TemplatesPage }))));
const CampaignsPage     = lazy(withChunkRetry(() => import("./pages/CampaignsPage").then(m => ({ default: m.CampaignsPage }))));
const AddressesPage     = lazy(withChunkRetry(() => import("./pages/AddressesPage").then(m => ({ default: m.AddressesPage }))));
const RoutingPage       = lazy(withChunkRetry(() => import("./pages/RoutingPage").then(m => ({ default: m.RoutingPage }))));
const EventsPage        = lazy(withChunkRetry(() => import("./pages/EventsPage").then(m => ({ default: m.EventsPage }))));
const AutomationsPage   = lazy(withChunkRetry(() => import("./pages/AutomationsPage").then(m => ({ default: m.AutomationsPage }))));
const ContactsPage      = lazy(withChunkRetry(() => import("./pages/ContactsPage").then(m => ({ default: m.ContactsPage }))));
const ListsPage         = lazy(withChunkRetry(() => import("./pages/ListsPage").then(m => ({ default: m.ListsPage }))));
const SegmentsPage      = lazy(withChunkRetry(() => import("./pages/SegmentsPage").then(m => ({ default: m.SegmentsPage }))));
const PlansPage         = lazy(withChunkRetry(() => import("./pages/PlansPage").then(m => ({ default: m.PlansPage }))));
const TeamPage          = lazy(withChunkRetry(() => import("./pages/TeamPage").then(m => ({ default: m.TeamPage }))));
const ApiKeysPage       = lazy(withChunkRetry(() => import("./pages/ApiKeysPage").then(m => ({ default: m.ApiKeysPage }))));
const WebhooksPage      = lazy(withChunkRetry(() => import("./pages/WebhooksPage").then(m => ({ default: m.WebhooksPage }))));
const SettingsPage      = lazy(withChunkRetry(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage }))));
const HelpPage          = lazy(withChunkRetry(() => import("./pages/HelpPage").then(m => ({ default: m.HelpPage }))));

/**
 * Routes.
 *
 * Every screen in the rail is now built: Overview, Logs, Events, Suppressions, Templates, Campaigns,
 * Automations, Domains, Addresses, Inbound & routing, Plans, API keys, Webhooks and Settings. Most
 * consume the public `/v1` contract; the three that cannot — API keys, Webhooks and Settings — use
 * the session `/api/*` routes on purpose, because minting a credential, silencing an event stream and
 * changing a password must be authorised by the session and never by an API key.
 *
 * "API reference" in the rail is an OUTBOUND link, not a route: the reference is published on the
 * marketing site at /docs/email-api so it can be read without an account, linked and indexed.
 *
 * This catch-all therefore no longer describes an unfinished product. It exists for a stale link or a
 * mistyped path, and says which of those it is rather than rendering a blank page — an empty screen
 * with no explanation is indistinguishable from a bug.
 *
 * A few CONTROLS remain deliberately inert (the workspace switcher, global search, the product
 * switcher). Each is inert because the server has no endpoint that could honour it, and each states
 * that reason on hover rather than pretending to work.
 */
/**
 * Shown in the content area while a route's chunk is in flight.
 *
 * Deliberately quiet and unlabelled rather than a spinner with "Loading…". On a warm cache this is on
 * screen for a handful of milliseconds, and a spinner that appears and vanishes that fast reads as a
 * flicker — it makes a fast navigation look broken. It holds the content area's height so the shell
 * does not reflow when the page arrives.
 *
 * `aria-busy` with a polite live region, because a sighted user sees the shell stay put and knows
 * something is coming; a screen-reader user otherwise gets silence between the click and the content.
 */
function PageLoading() {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      style={{ minHeight: "60vh", display: "flex", alignItems: "flex-start", paddingTop: 8 }}
    >
      <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
        Loading page
      </span>
    </div>
  );
}

/*
 * Renamed from `NotBuiltYet`, which contradicted its own copy: it renders "Page not found", which is what
 * an unmatched route means in a console where every screen exists. A name suggesting the product is
 * half-finished is the kind of thing that leaks into a screenshot or a stack trace.
 */
function PageNotFound() {
  return (
    <>
      <PageHeader title="Page not found" />
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)" }}>
        <EmptyState
          title="There is nothing at this address"
          description="Every screen in this console is reachable from the navigation on the left. If you followed a link to get here, it is out of date."
        />
      </div>
    </>
  );
}

/**
 * The authentication gate.
 *
 * ═══ Why the whole app is behind it, rather than each screen ══════════════════
 *
 * Every screen in this console reads `/v1`, and `/v1` needs a credential. Letting the shell render
 * without one means every panel independently discovers it is unauthenticated and shows its own
 * version of that — which is what the console did before sign-in existed: four "Not signed in" states
 * on one page and no way to act on any of them.
 *
 * Subscribed to `onSessionChange` rather than checked once, so a session ending mid-use (a refresh
 * token the server has rejected, a sign-out in another tab) drops straight back to this screen instead
 * of leaving a shell full of failing requests.
 */
export default function App() {
  const [signedIn, setSignedIn] = useState(() => isSignedIn());
  const [location, navigate] = useLocation();

  /*
   * The app rendered, so whatever we may have reloaded for is working.
   *
   * The chunk-recovery guard allows exactly one automatic reload per tab, to make a reload LOOP
   * impossible. Left set, that single allowance would be spent forever: one recovered deploy in the
   * morning would leave the tab unable to recover from the afternoon's. Reaching this point is the only
   * honest signal that recovery succeeded.
   */
  useEffect(() => { clearChunkReloadGuard(); }, []);
  /**
   * Which auth screen an arriving visitor sees.
   *
   * Defaults to sign-in, because most people opening this console already have an account. But a
   * visitor sent here by the marketing site's "Start with PulseAssist Email" has explicitly chosen to
   * SIGN UP, and landing them on a sign-in form asks them for a password they do not have — the exact
   * dead end the marketing CTA exists to avoid.
   *
   * Read from the URL rather than made a route, keeping the reasoning below intact: these two screens
   * toggle between each other and nothing else, so they do not need a router. `?signup=1` is enough to
   * carry an intent across the domain boundary from getpulseassist.com to this console, and it is
   * read once at mount so pressing "Sign in" afterwards still works normally.
   */
  const [authScreen, setAuthScreen] = useState<"signin" | "signup">(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      // `/signup` is accepted too: it is the URL people type, and it would otherwise hit the shell's
      // 404 once signed in — or a sign-in form when not.
      if (params.get("signup") !== null || window.location.pathname === "/signup") return "signup";
    } catch {
      // A non-browser or locked-down environment. Sign-in is the safe default.
    }
    return "signin";
  });

  useEffect(() => {
    const stop = onSessionChange(() => setSignedIn(isSignedIn()));
    /*
     * `storage` fires in OTHER tabs of this origin. Signing out in one tab should not leave another
     * showing a console it can no longer talk to — nor, more importantly, leave someone believing they
     * are signed out of a machine where a second tab is still authenticated.
     */
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === "pa-session-token") setSignedIn(isSignedIn());
    };
    window.addEventListener("storage", onStorage);
    return () => {
      stop();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  /*
   * Sign-in vs sign-up is local state, not a route.
   *
   * Both screens live entirely outside the authenticated shell, and the router below is only mounted
   * once there is a session. Putting them on routes would mean the unauthenticated app needed its own
   * router and its own catch-all, for two screens that toggle between each other and nothing else.
   */
  if (!signedIn) {
    return authScreen === "signup" ? (
      <SignUpPage
        onSignedUp={() => setSignedIn(true)}
        onSignIn={() => setAuthScreen("signin")}
      />
    ) : (
      <SignInPage
        onSignedIn={() => setSignedIn(true)}
        onCreateAccount={() => setAuthScreen("signup")}
      />
    );
  }

  /*
   * The Suspense boundary is INSIDE AppShell, and that placement is the point.
   *
   * Outside it, the whole window would blank while a page chunk arrived — every navigation would flash
   * the shell away and back, which reads as slower than the eager bundle even though less is being
   * downloaded. Inside, the rail, header and workspace chrome paint immediately and stay put, and only
   * the content area shows the pending state. That is also what makes the first paint after sign-in
   * feel immediate: the shell is in the entry chunk.
   */
  /*
   * ConfirmProvider wraps the shell, not each page.
   *
   * OUTSIDE the RouteErrorBoundary and the Suspense boundary deliberately. Inside either, the dialog
   * would unmount the moment a route threw or suspended — and "are you sure?" disappearing because a
   * lazy chunk started loading is exactly the kind of thing that makes a click feel unreliable.
   *
   * The signed-out screens return above and get no provider, which is correct: they have nothing to
   * confirm, and `useConfirm()` throws rather than falling back to a native dialog, so a future call
   * from there fails loudly in development instead of quietly shipping a `window.confirm`.
   */
  return (
    <ConfirmProvider>
    <AppShell>
      {/*
        `key={location}` is load-bearing: a boundary that has caught STAYS caught, so without it one
        broken screen would poison every route the user visited afterwards. Re-keying on navigation
        gives each screen a fresh boundary.

        Inside AppShell, so a failure leaves the rail and header on screen and the person can navigate
        away — which is what they want, and is impossible if the boundary replaced the whole window.
      */}
      <RouteErrorBoundary key={location} onGoHome={() => navigate("/")}>
        <Suspense fallback={<PageLoading />}>
          <Switch>
            <Route path="/" component={OverviewPage} />
            <Route path="/logs" component={LogsPage} />
            <Route path="/configuration" component={ConfigurationPage} />
            <Route path="/domains" component={DomainsPage} />
            <Route path="/suppressions" component={SuppressionsPage} />
            <Route path="/templates" component={TemplatesPage} />
            <Route path="/campaigns" component={CampaignsPage} />
            {/*
              Two routes, one component. `/mailboxes` is the receiving side (its own rail item under
              Compose); `/addresses` is the sending side. AddressesPage derives which from the path, so
              the rail's pathname-based active highlight works for both and Back moves between them.
            */}
            <Route path="/mailboxes" component={AddressesPage} />
            <Route path="/addresses" component={AddressesPage} />
            <Route path="/routing" component={RoutingPage} />
            <Route path="/events" component={EventsPage} />
            <Route path="/automations" component={AutomationsPage} />
            <Route path="/contacts" component={ContactsPage} />
            <Route path="/lists" component={ListsPage} />
            <Route path="/segments" component={SegmentsPage} />
            <Route path="/plans" component={PlansPage} />
            <Route path="/team" component={TeamPage} />
            <Route path="/keys" component={ApiKeysPage} />
            <Route path="/webhooks" component={WebhooksPage} />
            <Route path="/settings" component={SettingsPage} />
            <Route path="/help" component={HelpPage} />
            <Route component={PageNotFound} />
          </Switch>
        </Suspense>
      </RouteErrorBoundary>
    </AppShell>
    </ConfirmProvider>
  );
}
